import { useMemo, useState, type ReactElement } from "react";
import { Link as LinkIcon, Plus, Search } from "lucide-react";
import { Badge, Button, EmptyState, Select, Stack, Text, TextInput } from "@ux/primitives";
import { BottomActionBar, ConfirmAction, SplitPane, Toolbar } from "@ux/compositions";
import { useViewport } from "@ux/hooks";
import { useCanEdit } from "@shell/auth/useCanEdit";
import { useApiClient } from "@shell/apiClientContext";
import { matchesQuery } from "../common/matchesQuery";
import { useUserDirectorySearch, useUserDisplayNames } from "../users/hooks";
import { useAssets } from "../assets/hooks";
import { useWorkspaceDisplayNames } from "../workspaces/hooks";
import { describeApiError } from "../common/errors";
import { ExportDataButton } from "../common/ExportDataButton";
import { buildAssetAssignmentsExportCsv } from "../common/exportCsv";
import { useAssetAssignments, useCreateAssetAssignment, useReleaseAssetAssignment } from "./hooks";
import { exportAssetAssignments } from "./api";
import { AssetAssignmentsList } from "./AssetAssignmentsList";
import { AssetAssignmentForm, type AssetAssignmentFormMode } from "./AssetAssignmentForm";
import { targetKinds, type AssetAssignmentView } from "./types";

type Mode = { kind: "idle" } | { kind: "create" } | { kind: "view"; id: string };

export function AssetAssignmentsPage(): ReactElement {
    const { isPhone } = useViewport();
    const canEdit = useCanEdit();
    const client = useApiClient();
    const [search, setSearch] = useState("");
    const [targetKindFilter, setTargetKindFilter] = useState("");
    const [openOnly, setOpenOnly] = useState(true);
    const [mode, setMode] = useState<Mode>({ kind: "idle" });
    const [pendingRelease, setPendingRelease] = useState<AssetAssignmentView | null>(null);

    const assignmentsQuery = useAssetAssignments({
        assetId: "",
        userId: "",
        workspaceId: "",
        targetKind: targetKindFilter,
        openOnly,
    });
    const directorySearch = useUserDirectorySearch(search);
    const assetsQuery = useAssets({ q: "", sageOfficeId: "", type: "", includeDeleted: true });

    const createMutation = useCreateAssetAssignment();
    const releaseMutation = useReleaseAssetAssignment();

    const catalog = useMemo(() => assignmentsQuery.data ?? [], [assignmentsQuery.data]);
    const assets = useMemo(() => assetsQuery.data ?? [], [assetsQuery.data]);

    const assetIndex = useMemo(() => {
        const map = new Map<string, (typeof assets)[number]>();
        for (const a of assets) map.set(a.id, a);
        return map;
    }, [assets]);

    // Resolve every userId visible on the page through the shared HR
    // directory cache. Only User-target assignments carry a userId; the
    // others contribute nothing and are filtered out before the lookup
    // so we don't burn a cache entry on the empty-string sentinel.
    const userIdsOnPage = useMemo(
        () =>
            catalog
                .filter((r) => r.targetKind === "User")
                .map((r) => r.assignedToUserId ?? "")
                .filter((id) => id !== ""),
        [catalog],
    );
    const { map: userIndex } = useUserDisplayNames(userIdsOnPage);

    const workspaceIdsOnPage = useMemo(
        () =>
            catalog
                .filter((r) => r.targetKind === "Workspace")
                .map((r) => r.assignedToWorkspaceId ?? "")
                .filter((id) => id !== ""),
        [catalog],
    );
    const { map: workspaceIndex } = useWorkspaceDisplayNames(workspaceIdsOnPage);

    const directoryUserIds = useMemo(() => {
        const ids = new Set<string>();
        for (const user of directorySearch.data ?? []) ids.add(user.id.toLowerCase());
        return ids;
    }, [directorySearch.data]);

    const rows = useMemo(() => {
        const q = search.trim();
        if (q === "") return catalog;
        return catalog.filter((row) => {
            const userId = row.assignedToUserId ?? "";
            if (userId !== "" && directoryUserIds.has(userId.toLowerCase())) return true;
            const asset = assetIndex.get(row.assetId) ?? null;
            const user = userId !== "" ? (userIndex.get(userId) ?? null) : null;
            const workspaceId = row.assignedToWorkspaceId ?? "";
            const workspace = workspaceId !== "" ? (workspaceIndex.get(workspaceId) ?? null) : null;
            return matchesQuery(q, [
                row.assetId,
                asset?.assetTag,
                asset?.serialNumber,
                asset?.make,
                asset?.model,
                asset?.type,
                userId,
                user?.displayName,
                user?.email,
                user?.userPrincipalName,
                workspaceId,
                workspace?.name,
                workspace?.friendlyName,
                workspace?.floorplanElementId,
            ]);
        });
    }, [catalog, search, directoryUserIds, assetIndex, userIndex, workspaceIndex]);

    const selectedRecord = useMemo(() => {
        if (mode.kind !== "view") return null;
        return rows.find((r) => r.id === mode.id) ?? null;
    }, [mode, rows]);

    const formMode: AssetAssignmentFormMode | null = useMemo(() => {
        if (mode.kind === "create") return { kind: "create" };
        if (mode.kind === "view" && selectedRecord !== null)
            return { kind: "view", record: selectedRecord };
        return null;
    }, [mode, selectedRecord]);

    const isSaving = createMutation.isPending || releaseMutation.isPending;
    const formError =
        mode.kind === "create" && createMutation.isError
            ? describeApiError(createMutation.error)
            : null;

    function handleCreate(payload: Parameters<typeof createMutation.mutate>[0]): void {
        // Post-save → idle (back to list). Matches the rest of the admin
        // pages: a successful mutation drops the user at the master view
        // instead of stranding them on the freshly-created record.
        createMutation.mutate(payload, {
            onSuccess: () => setMode({ kind: "idle" }),
        });
    }

    function handleReleaseRequest(record: AssetAssignmentView): void {
        setPendingRelease(record);
    }

    function handleReleaseConfirmed(): void {
        if (pendingRelease === null) return;
        const record = pendingRelease;
        releaseMutation.mutate(
            { assetId: record.assetId, assignmentId: record.id },
            {
                onSuccess: () => {
                    setPendingRelease(null);
                    setMode({ kind: "idle" });
                },
            },
        );
    }

    return (
        <Stack direction="column" gap={0} grow style={{ overflow: "hidden" }}>
            <Toolbar
                leading={
                    <Stack direction="row" gap={2} align="center">
                        <Text role="heading">Asset Assignments</Text>
                        <Badge tone="neutral">{rows.length}</Badge>
                    </Stack>
                }
                trailing={
                    <Stack direction="row" gap={2} align="center" wrap>
                        <Search size={14} style={{ opacity: 0.55 }} />
                        <div style={{ width: 220 }}>
                            <TextInput
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Name, email, tag, or id…"
                            />
                        </div>
                        <div style={{ width: 140 }}>
                            <Select
                                value={targetKindFilter}
                                onChange={(e) => setTargetKindFilter(e.target.value)}
                                options={[
                                    { value: "", label: "Any target" },
                                    ...targetKinds.map((k) => ({ value: k, label: k })),
                                ]}
                            />
                        </div>
                        <Button
                            variant={openOnly ? "secondary" : "ghost"}
                            size="sm"
                            onClick={() => setOpenOnly((v) => !v)}
                        >
                            {openOnly ? "Open only" : "All"}
                        </Button>
                        <ExportDataButton
                            filenamePrefix="asset-assignments"
                            run={async () => {
                                const exported = await exportAssetAssignments(client, {
                                    targetKind: targetKindFilter,
                                    ...(openOnly ? { open: true } : {}),
                                });
                                return {
                                    csv: buildAssetAssignmentsExportCsv(exported.results),
                                    truncated: exported.truncated,
                                    rowCount: exported.results.length,
                                };
                            }}
                        />
                        {!isPhone && canEdit ? (
                            <Button
                                variant="primary"
                                size="sm"
                                leadingIcon={<Plus size={16} />}
                                onClick={() => setMode({ kind: "create" })}
                            >
                                New assignment
                            </Button>
                        ) : null}
                    </Stack>
                }
            />

            <SplitPane
                activeBelowDesktop={mode.kind === "idle" ? "master" : "detail"}
                master={
                    <AssetAssignmentsList
                        rows={rows}
                        assetIndex={assetIndex}
                        workspaceIndex={workspaceIndex}
                        userIndex={userIndex}
                        selectedId={mode.kind === "view" ? mode.id : null}
                        onSelect={(row) => setMode({ kind: "view", id: row.id })}
                        isLoading={assignmentsQuery.isPending}
                        error={
                            assignmentsQuery.isError
                                ? describeApiError(assignmentsQuery.error)
                                : null
                        }
                    />
                }
                detail={
                    formMode === null ? (
                        <EmptyState
                            icon={<LinkIcon size={32} />}
                            title={
                                canEdit
                                    ? "Pick or create an assignment"
                                    : "Pick an assignment to view"
                            }
                            description={
                                canEdit
                                    ? "Open an assignment from the list to release it, or start a new one."
                                    : "Open an assignment from the list to view its details."
                            }
                            action={
                                canEdit ? (
                                    <Button
                                        variant="primary"
                                        leadingIcon={<Plus size={16} />}
                                        onClick={() => setMode({ kind: "create" })}
                                    >
                                        New assignment
                                    </Button>
                                ) : undefined
                            }
                        />
                    ) : (
                        <AssetAssignmentForm
                            mode={formMode}
                            assets={assets}
                            onCreate={handleCreate}
                            onRelease={handleReleaseRequest}
                            onCancel={() => setMode({ kind: "idle" })}
                            isSaving={isSaving}
                            errorMessage={formError}
                        />
                    )
                }
            />

            {isPhone && canEdit && mode.kind === "idle" ? (
                <BottomActionBar>
                    <Button
                        variant="primary"
                        leadingIcon={<Plus size={16} />}
                        onClick={() => setMode({ kind: "create" })}
                    >
                        New assignment
                    </Button>
                </BottomActionBar>
            ) : null}

            <ConfirmAction
                open={pendingRelease !== null}
                title="Release this assignment?"
                description="The asset will become available again. The assignment record will remain in history with a release timestamp."
                confirmLabel="Release"
                destructive
                busy={releaseMutation.isPending}
                onCancel={() => setPendingRelease(null)}
                onConfirm={handleReleaseConfirmed}
            />
        </Stack>
    );
}
